/* ──────────────────────────────────────────────────────────────
 *  GeometryPanel — Card 3 Bottom (right column)
 *
 *  Hosts the 3D geometry viewer using Plotly Mesh3d.
 *  Manages geometry view flipping between ISO and supported
 *  cross-section views.
 *
 *  Note: the FlipButton control is rendered inside GeometryViewer
 *  now (bottom-right corner of the white panel), not in
 *  GeometryHeader. Sequencing/disable logic (flipConfig, viewSet,
 *  flipIndex, handleFlip, isGeneratedSample) is unchanged — only
 *  where the button visually lives has moved.
 * ────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';

import { SURFACE_BG } from '@/constants/colors';
import { useGeometry } from '@/hooks/useGeometry';
import type { UseGeometryResult } from '@/hooks/useGeometry';
import { useSampleStore } from '@/stores/useSampleStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { FLIP_CONFIGS } from '@/types/geometry';
import type { GeometryView } from '@/types/geometry';

import GeometryHeader from './GeometryHeader';
import GeometryViewer from './GeometryViewer';

const DEFAULT_VIEWS: GeometryView[] = ['iso', 'sxh'];

const GeometryPanel: React.FC = () => {
  const currentSelectedId = useSampleStore(
    (state) => state.currentSelectedId,
  );

  const genRowsById = useSampleStore(
    (state) => state.genRowsById,
  );

  const bushingType = useBushingStore(
    (state) => state.bushingType,
  );

  /*
   * Resolve the supported view sequence for the selected bushing type.
   *
   * Examples:
   * A1 / B1 / C1 / BYD EV Solid:
   *   iso -> sxh
   *
   * D1 / BYD EV Voided:
   *   iso -> axh -> sxh
   */
  const flipConfig = FLIP_CONFIGS[bushingType];

  const viewSet = useMemo<GeometryView[]>(
    () => flipConfig?.views ?? DEFAULT_VIEWS,
    [flipConfig],
  );

  const [flipIndex, setFlipIndex] = useState(0);

  const currentView: GeometryView =
    viewSet[flipIndex % viewSet.length] ?? 'iso';

  /*
   * Reset the geometry view whenever the selected bushing type changes.
   * This prevents the new bushing type from inheriting an unsupported
   * cross-section view from the previous bushing type.
   */
  useEffect(() => {
    setFlipIndex(0);
  }, [bushingType]);

  const handleFlip = useCallback(() => {
    setFlipIndex((previousIndex) => {
      return (previousIndex + 1) % viewSet.length;
    });
  }, [viewSet.length]);

  /*
   * Prefetch all geometry views supported by the current bushing type.
   *
   * Unsupported views remain disabled. When the user flips the view,
   * the required geometry should already be available in the query cache.
   */
  const isoResult = useGeometry({
    viewType: 'iso',
    prefetchEnabled: viewSet.includes('iso'),
  });

  const sxhResult = useGeometry({
    viewType: 'sxh',
    prefetchEnabled: viewSet.includes('sxh'),
  });

  const axhResult = useGeometry({
    viewType: 'axh',
    prefetchEnabled: viewSet.includes('axh'),
  });

  const resultMap: Record<GeometryView, UseGeometryResult> = {
    iso: isoResult,
    sxh: sxhResult,
    axh: axhResult,
  };

  const {
    mesh,
    isLoading,
    isFetching,
    isError,
  } = resultMap[currentView];

  const isGeneratedSample = Boolean(
    currentSelectedId &&
      currentSelectedId.startsWith('N') &&
      genRowsById[currentSelectedId],
  );

  /*
   * Do not render cached geometry when there is no generated sample.
   *
   * This prevents geometry belonging to a previous sample or bushing
   * type from remaining visible after reset, SOR clear, or type change.
   */
  const effectiveMesh = isGeneratedSample ? mesh : undefined;

  return (
    <Card
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '18px',
        overflow: 'hidden',
        minHeight: 0,
        backgroundColor: SURFACE_BG,
        p: 1.5,
        gap: 1,
      }}
    >
      <GeometryHeader />

      <GeometryViewer
        mesh={effectiveMesh}
        viewType={currentView}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        currentView={currentView}
        flipDisabled={!isGeneratedSample}
        onFlip={handleFlip}
      />
    </Card>
  );
};

export default React.memo(GeometryPanel);